import React from 'react';
import { Check, X, Trash2, ExternalLink, User, Calendar, AtSign, ListTodo, MessageSquare, Clock, Wrench, Star, CheckCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import type { Notification, NotificationType } from '../../types/notifications';
import { useMobileNavigate } from '../mobile/MobileNavigationGuard';
import { formatDistanceToNow } from 'date-fns';
import { getRelatedRecordPath } from '../../utils/relatedRecordNavigation';

// Get icon for notification type
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'mention':
      return AtSign;
    case 'task_assigned':
      return ListTodo;
    case 'task_due':
      return Clock;
    case 'message':
      return MessageSquare;
    case 'calendar_event_created':
    case 'calendar_event_updated':
    case 'calendar_event_cancelled':
      return Calendar;
    case 'service_assignment':
    case 'service_schedule':
      return Wrench;
    default:
      return User;
  }
}

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (read: boolean) => void;
  onDelete: () => void;
  showStar?: boolean;
  isStarred?: boolean;
  onToggleStar?: () => void;
  showResolve?: boolean;
  isResolved?: boolean;
  onToggleResolved?: () => void;
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  showStar = false,
  isStarred,
  onToggleStar,
  showResolve = false,
  isResolved = false,
  onToggleResolved,
}: NotificationItemProps) {
  const navigate = useMobileNavigate();
  const isUnread = !notification.read;
  const starredState = isStarred ?? notification.starred;

  const handleClick = () => {
    // Navigate to related entity
    const { module } = notification.related_to;
    const path = getRelatedRecordPath(notification.related_to);
    // Pass navigation state to show where user came from
    const fromState = module === 'messages' 
      ? { from: { label: 'Notifications', href: undefined } }
      : undefined;
    navigate(path, { state: fromState });
    // Mark as read when clicked
    if (!notification.read) {
      onMarkAsRead(true);
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead(!notification.read);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  const handleToggleStar = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleStar?.();
  };

  const handleToggleResolved = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleResolved?.();
  };

  const Icon = getNotificationIcon(notification.notification_type);
  const creatorName = notification.created_by_name;

  return (
    <div
      className={cn(
        'p-3 hover:bg-muted/50 transition-colors cursor-pointer group',
        isUnread && 'bg-primary/5'
      )}
      onClick={handleClick}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5',
          isUnread ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
        )}>
          <Icon className="h-4 w-4" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={cn('text-sm leading-snug', isUnread ? 'font-medium' : 'text-muted-foreground')}>
            {notification.message}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {creatorName && (
              <>
                <span className="text-xs text-muted-foreground">
                  by {creatorName}
                </span>
                <span className="text-xs text-muted-foreground/50">•</span>
              </>
            )}
            {starredState ? (
              <Star className="h-3 w-3 text-warning/70 fill-current" aria-label="Starred notification" />
            ) : null}
            <span className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </span>
            {showResolve && isResolved ? <Badge variant="secondary" className="text-[10px] py-0">Resolved</Badge> : null}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {showResolve && onToggleResolved ? (
            <Button
              variant="ghost"
              size="sm"
              className={cn('h-7 w-7 p-0', isResolved && 'text-success')}
              onClick={handleToggleResolved}
              title={isResolved ? 'Mark as unresolved' : 'Mark as resolved'}
            >
              <CheckCheck className="h-3 w-3" />
            </Button>
          ) : null}
          {showStar && onToggleStar ? (
            <Button
              variant="ghost"
              size="sm"
              className={cn('h-7 w-7 p-0', starredState && 'text-warning')}
              onClick={handleToggleStar}
              title={starredState ? 'Unstar notification' : 'Star notification'}
            >
              <Star className={cn('h-3 w-3', starredState && 'fill-current')} />
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={handleMarkAsRead}
            title={notification.read ? 'Mark as unread' : 'Mark as read'}
          >
            {notification.read ? (
              <Check className="h-3 w-3" />
            ) : (
              <X className="h-3 w-3" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive"
            onClick={handleDelete}
            title="Delete notification"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            title="View related entity"
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}
